import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, User, Store, Truck, CreditCard, Ticket, Star, Activity, MapPin, Edit } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils-helpers';

export default function OrderDetail360() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/admin/orders/${orderId}/360`);
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  if (loading) return <div className="p-6">Loading...</div>;
  if (!data) return <div className="p-6">Order not found</div>;

  const order = data.order;
  const payment = data.payment_summary;

  const getStatusColor = (status) => {
    const statusColors = {
      delivered: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      in_transit: 'bg-sky-50 text-sky-800 border-sky-200',
      picked_up: 'bg-violet-50 text-violet-800 border-violet-200',
      assigned: 'bg-indigo-50 text-indigo-800 border-indigo-200',
      ready: 'bg-teal-50 text-teal-800 border-teal-200',
      preparing: 'bg-amber-50 text-amber-800 border-amber-200',
      confirmed: 'bg-blue-50 text-blue-800 border-blue-200',
      placed: 'bg-slate-50 text-slate-800 border-slate-200',
      cancelled: 'bg-rose-50 text-rose-800 border-rose-200'
    };
    return statusColors[status] || 'bg-slate-50 text-slate-800 border-slate-200';
  };

  const handleStatusUpdate = async () => {
    if (!newStatus || !statusReason.trim()) {
      toast.error('Please select a status and provide a reason');
      return;
    }

    setUpdating(true);
    try {
      await api.put(`/admin/orders/${orderId}/status`, {
        status: newStatus,
        reason: statusReason
      });
      toast.success(`Order status updated to ${newStatus}`);
      setShowStatusDialog(false);
      setNewStatus('');
      setStatusReason('');
      
      // Refresh data
      const res = await api.get(`/admin/orders/${orderId}/360`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/admin/orders')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold" data-font="display">360° Order View</h1>
              {order.order_number && (
                <Badge 
                  variant="outline" 
                  className="font-mono text-sm border-[hsl(351,96%,31%)] text-[hsl(351,96%,31%)] bg-[hsl(351,96%,31%,0.05)]"
                  data-testid="order-id-badge"
                >
                  {order.order_number}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">Complete order details for {order.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span 
            className={`inline-flex items-center rounded-md border px-4 py-2 text-sm font-semibold transition-colors duration-200 cursor-default ${getStatusColor(order.status)}`}
          >
            {order.status.replace('_', ' ').toUpperCase()}
          </span>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              setNewStatus(order.status);
              setShowStatusDialog(true);
            }}
            data-testid="override-status-button"
          >
            <Edit className="h-4 w-4 mr-2" />
            Override Status
          </Button>
        </div>
      </div>

      {/* Order Summary Card */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Customer */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <User className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold">Customer</h3>
              </div>
              {data.customer ? (
                <div>
                  <p className="font-medium">{data.customer.name}</p>
                  <p className="text-sm text-muted-foreground">{data.customer.email}</p>
                  {data.customer.phone && (
                    <p className="text-sm text-muted-foreground">{data.customer.phone}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No customer info</p>
              )}
            </div>

            {/* Vendor */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Store className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold">Vendor</h3>
              </div>
              {data.vendor ? (
                <div>
                  <p className="font-medium">{data.vendor.name}</p>
                  <p className="text-sm text-muted-foreground">{data.vendor.category}</p>
                  <p className="text-sm text-muted-foreground">{data.vendor.address}</p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No vendor info</p>
              )}
            </div>

            {/* Driver */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Truck className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold">Driver</h3>
              </div>
              {data.driver ? (
                <div>
                  <p className="font-medium">{data.driver.name}</p>
                  <p className="text-sm text-muted-foreground">{data.driver.email}</p>
                  {data.driver.vehicle_type && (
                    <p className="text-sm text-muted-foreground">{data.driver.vehicle_type}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Not assigned</p>
              )}
            </div>
          </div>

          {/* Delivery Address */}
          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-5 w-5 text-muted-foreground" />
              <h3 className="font-semibold">Delivery Address</h3>
            </div>
            <p className="text-sm">{order.delivery_address}</p>
            {order.notes && (
              <p className="text-sm text-muted-foreground mt-2">Notes: {order.notes}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="items" className="w-full">
        <TabsList>
          <TabsTrigger value="items">Order Items</TabsTrigger>
          <TabsTrigger value="payment"><CreditCard className="h-4 w-4 mr-2" />Payment</TabsTrigger>
          <TabsTrigger value="timeline"><Activity className="h-4 w-4 mr-2" />Timeline</TabsTrigger>
          {data.ticket && <TabsTrigger value="ticket"><Ticket className="h-4 w-4 mr-2" />Ticket</TabsTrigger>}
          {data.review && <TabsTrigger value="review"><Star className="h-4 w-4 mr-2" />Review</TabsTrigger>}
        </TabsList>

        {/* Order Items */}
        <TabsContent value="items" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Items Ordered</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium">{formatCurrency(item.price * item.quantity)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Details */}
        <TabsContent value="payment" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Payment Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatCurrency(payment.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Fee</span>
                  <span>{formatCurrency(payment.delivery_fee)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Fee</span>
                  <span>{formatCurrency(payment.service_fee)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-3 border-t">
                  <span>Total</span>
                  <span>{formatCurrency(payment.total)}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <span className="text-sm text-muted-foreground">Payment Method</span>
                  <Badge>{payment.payment_method}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Payment Status</span>
                  <Badge className={payment.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {payment.payment_status}
                  </Badge>
                </div>
              </div>

              {/* Transactions */}
              {data.transactions.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-semibold mb-3">Transaction History</h4>
                  <div className="space-y-2">
                    {data.transactions.map(txn => (
                      <div key={txn.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{txn.type.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(txn.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{formatCurrency(txn.amount)}</p>
                          <Badge className={txn.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {txn.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Timeline */}
        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Order Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 border-l-2 border-primary">
                  <Activity className="h-5 w-5 text-primary mt-1" />
                  <div>
                    <p className="font-medium">Order {order.status}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(order.updated_at || order.created_at)}</p>
                  </div>
                </div>
                {order.delivered_at && (
                  <div className="flex items-start gap-3 p-3 border-l-2 border-green-500">
                    <Activity className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <p className="font-medium">Order Delivered</p>
                      <p className="text-sm text-muted-foreground">{formatDate(order.delivered_at)}</p>
                    </div>
                  </div>
                )}
                {order.confirmed_at && (
                  <div className="flex items-start gap-3 p-3 border-l-2 border-blue-500">
                    <Activity className="h-5 w-5 text-blue-600 mt-1" />
                    <div>
                      <p className="font-medium">Order Confirmed</p>
                      <p className="text-sm text-muted-foreground">{formatDate(order.confirmed_at)}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3 p-3 border-l-2 border-gray-300">
                  <Activity className="h-5 w-5 text-gray-600 mt-1" />
                  <div>
                    <p className="font-medium">Order Placed</p>
                    <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                  </div>
                </div>
                {data.activity_timeline.map(log => (
                  <div key={log.id} className="flex items-start gap-3 p-3 border-l-2 border-gray-200">
                    <Activity className="h-4 w-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-sm font-medium">{log.action}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(log.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ticket (if exists) */}
        {data.ticket && (
          <TabsContent value="ticket" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Related Support Ticket</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{data.ticket.id}: {data.ticket.subject}</p>
                      <p className="text-sm text-muted-foreground">{data.ticket.category}</p>
                    </div>
                    <Badge className={data.ticket.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {data.ticket.status}
                    </Badge>
                  </div>
                  <p className="text-sm">{data.ticket.description}</p>
                  <p className="text-xs text-muted-foreground">Created: {formatDate(data.ticket.created_at)}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Review (if exists) */}
        {data.review && (
          <TabsContent value="review" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Customer Review</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`h-5 w-5 ${i < data.review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                    ))}
                    <span className="ml-2 font-medium">{data.review.rating}/5</span>
                  </div>
                  <p className="text-sm">{data.review.comment}</p>
                  <p className="text-xs text-muted-foreground">Posted: {formatDate(data.review.created_at)}</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Status Override Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override Order Status</DialogTitle>
            <DialogDescription>
              Manually change the order status. This action will be logged and requires a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-status">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger id="new-status">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="placed">Placed</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="preparing">Preparing</SelectItem>
                  <SelectItem value="ready">Ready for Pickup</SelectItem>
                  <SelectItem value="assigned">Assigned to Driver</SelectItem>
                  <SelectItem value="picked_up">Picked Up</SelectItem>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status-reason">Reason for Override</Label>
              <Textarea
                id="status-reason"
                placeholder="Explain why you're changing the status..."
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This reason will be logged for audit purposes.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)} disabled={updating}>
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate} disabled={updating || !newStatus || !statusReason.trim()}>
              {updating ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
